import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { StressResultsService } from '../../services/stress-results.service';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { lastValueFrom } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { SingleLoadResultsUi } from '../../models/perivis-results-state.model';
import { Store } from '@ngneat/elf';
import { unitsLib } from 'src/app/core/services/unit-library';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';

@Component({
  selector: 'app-single-load-collapse',
  templateUrl: './single-load-collapse.component.html',
  styles: [],
  standalone: false
})
export class SingleLoadCollapseComponent implements OnInit, OnDestroy {
  public collapseResults: any[];
  public loadCases: any[] = [];
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public modes: SelectItem[] = [
    { label: 'Single', value: 'single' },
    { label: 'Multiple', value: 'multiple' }
  ];
  public errorMsg: string;
  public results: any[];
  public depthView: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public isLoading = signal<boolean>(true);
  public selectedMode: string = 'single';
  public selectedResultDisplay: string = 'plot';
  public selectedDepthView: string = 'md';
  public yAxisTitle = '';
  public xAxisTitle = '';
  public tableName: string;
  public userUnits: UserUnitsModel;
  public plotName: string;
  public signalRChange: boolean;
  public longLengthUnit: string;
  public pressureUnit: string;
  public columnDefs: { header: string, field: string }[];
  public columnDefinitions: any[];

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.collapseResultsStore = this._storeService.createStore(this.componentId, new SingleLoadResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedMode = this.collapseResultsStore.state.selectedMode || 'single';
    this.selectedResultDisplay = this.collapseResultsStore.state.selectedResultDisplay || 'plot';
    this.selectedDepthView = this.collapseResultsStore.state.selectedDepthView || 'md';
  }
  get componentId(): string {
    return this._componentId;
  }
  public collapseResultsStore: Store;

  private selectedString: string;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.longLengthUnit = this.userUnits.longLengths;
    this.pressureUnit = unitsLib[this.userUnits.pressure].symbol;
    this.xAxisTitle = `Collapse Pressure (${this.pressureUnit})`;
    this.yAxisTitle = `MD (${this.longLengthUnit})`;

    // this.refreshResults(true, false);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this._storeService.watchSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS).subscribe(appIds => {
      this.selectedString = appIds.tubularId;
      this.refreshResults(true, false);
    });

    this.columnDefs = [
      { header: 'Collapse Load', field: 'collapseLoad' },
      { header: 'API Collapse Rating', field: 'apiCollapseRating' },
    ];
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Collapse Load (${this.pressureUnit})`, field: 'collapseLoad',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `API Collapse (${this.pressureUnit})`, field: 'apiCollapseRating',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS
      || data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds.tubularId;
      this.refreshResults(true, true);
    }
  }

  public async refreshResults(isLoading: boolean, calledFromSignalR: boolean, calledFromMode?: boolean): Promise<void> {
    this.isLoading.set(isLoading);

    this.xAxisTitle = `Collapse Pressure (${this.userUnits?.pressure})`;

    if ((!this.collapseResults || calledFromSignalR) && !calledFromMode) {
      try {
        this.collapseResults = await lastValueFrom(this._stressResultsService.getCollapseResults());
        this.signalRChange = true;
      } catch {
        this.errorMsg = "Well Configuration Missing: Please specify at least one string";
        this.isLoading.set(false);
        return;
      }
    }

    if (this.collapseResults && (this.collapseResults.length === 0 || this.collapseResults[0].loadCaseName === '')) {
      this.errorMsg = "Please calculate PERIVIS to see results";
      this.columnDefs = [];
      this.results = [];
      this.isLoading.set(false);
      return;
    } else {
      this.errorMsg = null;
    }

    if (this.collapseResultsStore.state.selectedMode == "single") {
      this.handleSingleCollapseLoad(this.collapseResults);
    } else {
      this.handleMultiCollapseLoad(this.collapseResults)
    }

    this.isLoading.set(false);
  }

  onModeChange(e): void {
    this.selectedMode = e.value;
    this.collapseResultsStore.update(state => ({ ...state, selectedMode: e.value }));
    if (this.collapseResultsStore.state.selectedMode == "multiple") {
      this.collapseResultsStore.update(state => ({ ...state, selectedLoadCase: { label: 'Collapse Load', value: 'collapseLoad' } }));
    } else {
      // this.xAxisTitle = `Collapse Pressure (psi)`;
    }

    this.plotName = this.setPlotName();
    this.refreshResults(true, false, true);
  }

  onSelectedResultDisplayUpdated(e): void {
    this.selectedResultDisplay = e.value;
    this.collapseResultsStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
  }

  setLoadCase(e): void {
    if (this.collapseResultsStore.state.mode === "single") {
      this.collapseResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.results = e.value.value.collapseResults;
    } else {
      this.collapseResultsStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.refreshResults(true, false, false);
    }
  }

  setPlotName(): string {
    return 'barlowCollapsePlot - ' + this.selectedString + ' - ' + this.collapseResultsStore.state.selectedMode + ' ' + this.collapseResultsStore.state.selectedLoadCase.label;
  }


  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.collapseResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  private formatDecimal(value: any, divisor: number): string {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  private handleSingleCollapseLoad(collapseResults: any[]) {
    this.columnDefs = [
      { header: 'Collapse Load', field: 'collapseLoad' },
      { header: 'API Collapse Rating', field: 'apiCollapseRating' },
    ];
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `Collapse Load (${this.userUnits?.pressure})`, field: 'collapseLoad',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `API Collapse (${this.userUnits?.pressure})`, field: 'apiCollapseRating',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];

    this.results = [];

    this.loadCases = collapseResults?.map(lc => {
      return { label: lc.loadCaseName, value: lc };
    }) ?? [];
    if (!this.collapseResultsStore.state.selectedLoadCase || !this.loadCases.find(l => l.value.tubularStringId == this.collapseResultsStore.state.selectedLoadCase?.value.tubularStringId)) {
      this.collapseResultsStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
      this.plotName = this.setPlotName();
    }
    this.results = this.collapseResultsStore.state.selectedLoadCase?.value.collapseResults || [];
  }

  private handleMultiCollapseLoad(collapseResults: any[]) {
    this.columnDefinitions.splice(2, this.columnDefinitions.length);
    this.columnDefs = [];
    this.results = [];

    this.loadCases = [
      { label: 'Collapse Load', value: 'collapseLoad' },
      { label: 'API Collapse Rating', value: 'apiCollapseRating' }
    ]

    this.xAxisTitle = this.collapseResultsStore.state.selectedLoadCase.value === 'collapseLoad' ? `Collapse Load (${this.pressureUnit})` : `API Collapse Rating (${this.pressureUnit})`;

    collapseResults.forEach(load => {
      const loadName: string = load.loadCaseName + ` (${this.pressureUnit})`;
      const loadNameCamel = toCamelCaseString(load.loadCaseName);

      this.columnDefs.push({ header: load.loadCaseName, field: loadNameCamel });

      this.columnDefinitions.push({
        header: loadName,
        field: loadNameCamel,
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      });

      const backupArray = [];

      load.collapseResults.forEach(loadResult => {
        const result = {
          [loadNameCamel]: loadResult[this.collapseResultsStore.state.selectedLoadCase.value],
          measuredDepth: loadResult.measuredDepth,
          trueVerticalDepth: loadResult.trueVerticalDepth
        }

        if (collapseResults[0].loadCaseName === load.loadCaseName) {
          this.results.push(result);
        } else {
          backupArray.push(result);
        }
      });

      this.plotName = this.setPlotName();

      this.results = this.results.map(x => {
        const otherLoad = backupArray.find(e => e.measuredDepth === x.measuredDepth)
        return { ...x, ...otherLoad }
      });
    });
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
